import { useState } from "react";
import { NavLink } from "react-router-dom";
import { MutationError, latestError, ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import { Funnel, TONE_MARKER } from "../today/charts";
import type { LifecycleStage, OpportunityRow, SuppressedItem } from "./types";
import {
  useCreateTask,
  useLifecycle,
  useOpportunities,
  useSavedSegmentHouseholds,
  useSavedSegments,
  useSnoozeOpportunity,
  useSuppressed,
  useSystemSegmentHouseholds,
  useSystemSegments,
  useTasks,
  useUpdateTask,
} from "./use-relationships";

// İLİŞKİ — the CRM relationship cockpit (CR-3). A calm, Turkish-first read that
// folds the advisor's existing relationship signals into one surface, drawn on OUR
// design system (`k-`/`surface`/`book-list` kit — NO new palette). Three tabs:
//  · Fırsatlar — the book-wide OPEN opportunity list (masked, paginated).
//  · Segmentler — the system segment counts + the org's saved segments (CR-1b);
//    picking one previews its own-scoped households (masked, capped).
//  · Görevlerim — the advisor's OWN personal tasks (create + settle).
// Everything is own-scope + masked; COMMISSION APPEARS NOWHERE.

const OPP_TYPE_LABEL: Record<OpportunityRow["opportunityType"], MessageKey> = {
  UNINSURED_VEHICLE: "rel.opp.type.uninsured",
  KASKO_GAP: "rel.opp.type.kasko",
  LAPSED_NOT_RENEWED: "rel.opp.type.lapsed",
};
// Amber ONLY for the duty-of-care-flavoured gaps (uninsured / lapsed); KASKO gap is
// a neutral depth opportunity. Colour never carries meaning alone — the badge text
// names the type.
const OPP_TYPE_BADGE: Record<OpportunityRow["opportunityType"], string> = {
  UNINSURED_VEHICLE: "k-badge--warning",
  KASKO_GAP: "k-badge--standard",
  LAPSED_NOT_RENEWED: "k-badge--warning",
};
// CR-6 — the "Neden burada" one-line reason per gap type (the engine's own signal,
// restated for the advisor). The server authors the machine reason; the console
// owns the human copy.
const OPP_WHY: Record<OpportunityRow["opportunityType"], MessageKey> = {
  UNINSURED_VEHICLE: "rel.opp.why.uninsured",
  KASKO_GAP: "rel.opp.why.kasko",
  LAPSED_NOT_RENEWED: "rel.opp.why.lapsed",
};

type Tab = "lifecycle" | "opportunities" | "segments" | "tasks" | "suppressed";

export function RelationshipCockpit() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>("lifecycle");

  const tabs: { key: Tab; label: MessageKey }[] = [
    { key: "lifecycle", label: "rel.tab.lifecycle" },
    { key: "opportunities", label: "rel.tab.opportunities" },
    { key: "segments", label: "rel.tab.segments" },
    { key: "tasks", label: "rel.tab.tasks" },
    { key: "suppressed", label: "rel.tab.suppressed" },
  ];

  return (
    <section className="surface" data-testid="relationships">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("rel.heading")}</h1>
          <p className="surface__subtitle">{t("rel.subtitle")}</p>
        </div>
      </header>

      {/* Governance line (design: "Komisyon yok · Care-hold'da satış yok" — the
          brand's standing promise, always visible on the relationship surface). */}
      <p className="rel-gov-line" data-testid="rel-gov">
        <Icon name="protection" size={14} /> {t("rel.gov")}
      </p>

      <div className="k-tabs" role="tablist" data-testid="rel-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={tab === tb.key}
            className={`k-tab ${tab === tb.key ? "k-tab--active" : ""}`}
            onClick={() => setTab(tb.key)}
            data-testid={`rel-tab-${tb.key}`}
          >
            {t(tb.label)}
          </button>
        ))}
      </div>

      {tab === "lifecycle" && <LifecycleTab />}
      {tab === "opportunities" && <OpportunitiesTab />}
      {tab === "segments" && <SegmentsTab />}
      {tab === "tasks" && <TasksTab />}
      {tab === "suppressed" && <SuppressedTab />}
    </section>
  );
}

// ── Yaşam döngüsü ──────────────────────────────────────────────────────────────
const LC_LABEL: Record<LifecycleStage, MessageKey> = {
  PROSPECT: "rel.lc.prospect",
  INTERESTED: "rel.lc.interested",
  CUSTOMER: "rel.lc.customer",
  RENEWAL: "rel.lc.renewal",
  RISK: "rel.lc.risk",
};
// Amber ONLY at Yenileme (a soft nudge), red ONLY at Risk (the genuine-attention
// tone); the earlier stages stay neutral — colour never carries the stage alone.
const LC_TONE: Record<LifecycleStage, "neutral" | "amber" | "red"> = {
  PROSPECT: "neutral",
  INTERESTED: "neutral",
  CUSTOMER: "neutral",
  RENEWAL: "amber",
  RISK: "red",
};
// Chart tone → an EXISTING k-badge primitive (no new palette).
const TONE_BADGE: Record<"amber" | "neutral" | "red", string> = {
  amber: "k-badge--warning",
  neutral: "k-badge--standard",
  red: "k-badge--priority",
};

function LifecycleTab() {
  const { t } = useT();
  const { data, isLoading, error, refetch, isFetching } = useLifecycle();

  if (isLoading) return <Loading label={t("rel.loading")} testId="rel-lc-loading" />;
  if (error)
    return (
      <ErrorNote
        detail={error instanceof ProblemError ? error.problem.detail : t("rel.error.detail")}
        onRetry={() => void refetch()}
        busy={isFetching}
        testId="rel-lc-error"
      />
    );
  if (!data) return null;

  if (data.total === 0)
    return (
      <Empty
        icon="household"
        title={t("rel.lc.empty.title")}
        desc={t("rel.lc.empty.desc")}
        testId="rel-lc-empty"
      />
    );

  const rows = data.stages.map((s) => ({
    key: s.stage,
    tone: LC_TONE[s.stage],
    label: t(LC_LABEL[s.stage]),
    value: s.count,
  }));

  return (
    <div className="k-card gunum-widget" data-testid="rel-lifecycle">
      <div className="gunum-widget__head">
        <span className="k-stat__icon k-stat__icon--blue">
          <Icon name="household" size={18} />
        </span>
        <span className="gunum-widget__title">{t("rel.lc.title")}</span>
      </div>

      <Funnel
        rows={rows}
        ariaLabel={`${t("rel.lc.title")}: ${rows.map((r) => `${r.label} ${r.value}`).join(", ")}`}
        testId="rel-lc-funnel"
      />
      <p className="gunum-note">{t("rel.lc.note")}</p>

      {/* Per-stage masked household preview (capped by the read). */}
      {data.stages
        .filter((s) => s.households.length > 0)
        .map((s) => (
          <div key={s.stage} className="rel-lc-stage" data-testid={`rel-lc-stage-${s.stage}`}>
            <span className={`k-badge ${TONE_BADGE[LC_TONE[s.stage]]}`}>
              <span aria-hidden="true">{TONE_MARKER[LC_TONE[s.stage]]} </span>
              {t(LC_LABEL[s.stage])} · {s.count}
            </span>
            <ul className="book-list">
              {s.households.map((h) => (
                <li key={h.id} className="k-card k-card--pad-sm book-row">
                  <NavLink to={`/households/${h.id}`} className="book-link">
                    {h.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

// A default snooze target (+7 days) as a YYYY-MM-DD value for the date input.
function defaultSnoozeDate(): string {
  const d = new Date(Date.now() + 7 * 86_400_000);
  return d.toISOString().slice(0, 10);
}

// One opportunity row: household + "Neden burada" + urgency, plus the mockup's two
// actions — "Öneri hazırla" (deep-link to the Öneri flow, household preselected) and
// "Ertele" (CR-7 snooze → the row drops from the list until the chosen date). NO
// commission; a snooze is not a customer contact.
function OpportunityRowItem({ row }: { row: OpportunityRow }) {
  const { t } = useT();
  const snooze = useSnoozeOpportunity();
  const [deferring, setDeferring] = useState(false);
  const [until, setUntil] = useState(defaultSnoozeDate);

  const confirmSnooze = () => {
    if (!until) return;
    // End-of-day of the chosen date, as an ISO instant (the server requires a future one).
    snooze.mutate(
      { id: row.id, until: new Date(`${until}T23:59:59`).toISOString() },
      { onSuccess: () => setDeferring(false) },
    );
  };

  return (
    <li className="k-card k-card--pad-sm book-row" data-testid={`rel-opp-${row.id}`}>
      <div className="book-row__main">
        <NavLink to={`/households/${row.householdId}`} className="book-link">
          {row.householdName}
        </NavLink>
        <p className="rel-opp-why" data-testid={`rel-opp-why-${row.id}`}>
          {t(OPP_WHY[row.opportunityType])}
          {row.reason.line ? ` · ${row.reason.line}` : ""}
        </p>
      </div>
      <span className={`k-badge ${OPP_TYPE_BADGE[row.opportunityType]}`}>
        {t(OPP_TYPE_LABEL[row.opportunityType])}
      </span>
      <span
        className={`k-chip ${row.reason.daysToExpiry <= 7 ? "k-chip--amber" : ""}`}
        data-testid={`rel-opp-urgency-${row.id}`}
      >
        <Icon name="calendar" size={14} />
        {row.reason.daysToExpiry} {t("rel.opp.daysLeft")}
      </span>
      <div className="rel-opp-actions">
        <NavLink
          to={`/recommendations?householdId=${row.householdId}&name=${encodeURIComponent(row.householdName)}`}
          className="k-btn k-btn--soft k-btn--sm"
          data-testid={`rel-opp-prepare-${row.id}`}
        >
          {t("rel.opp.prepare")}
        </NavLink>
        {!deferring ? (
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--sm"
            onClick={() => setDeferring(true)}
            data-testid={`rel-opp-defer-${row.id}`}
          >
            {t("rel.opp.defer")}
          </button>
        ) : (
          <span className="rel-opp-defer-form">
            <input
              type="date"
              className="k-input k-input--sm"
              value={until}
              min={defaultSnoozeDate()}
              onChange={(e) => setUntil(e.target.value)}
              data-testid={`rel-opp-defer-date-${row.id}`}
              aria-label={t("rel.opp.defer.until")}
            />
            <button
              type="button"
              className="k-btn k-btn--sm"
              disabled={snooze.isPending || !until}
              onClick={confirmSnooze}
              data-testid={`rel-opp-defer-confirm-${row.id}`}
            >
              {t("rel.opp.defer.confirm")}
            </button>
            {/* The server rejects a non-future date; that rejection was invisible,
                so the panel simply stayed open with no explanation. */}
            <MutationError
              error={snooze.error}
              fallback={t("crm.actionFailed")}
              offline={t("common.offline")}
            />
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--sm"
              onClick={() => setDeferring(false)}
              data-testid={`rel-opp-defer-cancel-${row.id}`}
            >
              {t("rel.opp.defer.cancel")}
            </button>
          </span>
        )}
      </div>
    </li>
  );
}

// ── Fırsatlar ────────────────────────────────────────────────────────────────
function OpportunitiesTab() {
  const { t } = useT();
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOpportunities();

  if (isLoading) return <Loading label={t("rel.loading")} testId="rel-opp-loading" />;
  if (error)
    return (
      <ErrorNote
        detail={error instanceof ProblemError ? error.problem.detail : t("rel.error.detail")}
        onRetry={() => void refetch()}
        busy={isFetching}
        testId="rel-opp-error"
      />
    );

  const rows = data?.pages.flatMap((p) => p?.data ?? []) ?? [];
  if (rows.length === 0)
    return (
      <Empty
        icon="suggestion"
        title={t("rel.opp.empty.title")}
        desc={t("rel.opp.empty.desc")}
        testId="rel-opp-empty"
      />
    );

  return (
    <div className="k-card gunum-widget" data-testid="rel-opp-list">
      <ul className="book-list">
        {rows.map((row) => (
          <OpportunityRowItem key={row.id} row={row} />
        ))}
      </ul>
      {hasNextPage && (
        <div className="k-note__actions">
          <button
            type="button"
            className="k-btn k-btn--soft k-btn--sm"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
            data-testid="rel-opp-more"
          >
            {t("rel.opp.more")}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Bastırılanlar (#262 · §1.3 "sessiz bastırma yok") ────────────────────────
// A touch the engine HELD BACK is shown with its reason, so it is visibly withheld
// rather than merely absent. Only marketing-basis touches can be suppressed —
// duty-of-care always goes through (§1.9), so nothing safety-critical hides here.
const SUPPRESSED_REASON_LABEL: Record<string, MessageKey> = {
  NO_CONSENT: "rel.sup.noConsent",
  CARE_HOLD: "rel.sup.careHold",
  COOL_OFF: "rel.sup.coolOff",
  TOUCH_CAP: "rel.sup.touchCap",
};

function SuppressedTab() {
  const { t } = useT();
  const q = useSuppressed();
  const rows = q.data ?? [];

  if (q.isLoading) return <Loading label={t("rel.loading")} testId="rel-sup-loading" />;
  if (q.error)
    return (
      <ErrorNote
        detail={q.error instanceof ProblemError ? q.error.problem.detail : t("rel.error.detail")}
        onRetry={() => void q.refetch()}
        busy={q.isFetching}
        testId="rel-sup-error"
      />
    );
  if (rows.length === 0)
    return (
      <Empty
        icon="tasks"
        title={t("rel.sup.empty.title")}
        desc={t("rel.sup.empty.desc")}
        testId="rel-sup-empty"
      />
    );

  return (
    <div className="k-card gunum-widget" data-testid="rel-suppressed">
      <p className="rel-opp-why">{t("rel.sup.intro")}</p>
      <ul className="book-list">
        {rows.map((r: SuppressedItem) => (
          <li key={r.id} className="k-card k-card--pad-sm book-row" data-testid={`rel-sup-${r.id}`}>
            <div className="book-row__main">
              <NavLink to={`/households/${r.householdId}`} className="book-link">
                {r.householdName}
              </NavLink>
              <p className="rel-opp-why">{r.itemType}</p>
            </div>
            <span className="k-badge k-badge--warning">
              {r.suppressedReason
                ? t(SUPPRESSED_REASON_LABEL[r.suppressedReason] ?? "rel.sup.other")
                : t("rel.sup.other")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Segmentler ───────────────────────────────────────────────────────────────
type Picked = { kind: "system" | "saved"; key: string; label: string } | null;

function SegmentsTab() {
  const { t } = useT();
  const system = useSystemSegments();
  const saved = useSavedSegments();
  const [picked, setPicked] = useState<Picked>(null);

  return (
    <div className="rel-two-col">
      <div className="k-card gunum-widget" data-testid="rel-segments">
        <div className="gunum-widget__head">
          <span className="k-stat__icon k-stat__icon--blue">
            <Icon name="scorecard" size={18} />
          </span>
          <span className="gunum-widget__title">{t("rel.seg.system.title")}</span>
        </div>
        {system.isLoading && <Loading label={t("rel.loading")} testId="rel-seg-sys-loading" />}
        {system.error != null && (
          <ErrorNote
            detail={t("crm.loadFailed")}
            onRetry={() => void system.refetch()}
            busy={system.isFetching}
            testId="rel-seg-sys-error"
          />
        )}
        {system.data && (
          <ul className="book-list">
            {system.data.map((s) => (
              <li key={s.key} className="k-card k-card--pad-sm book-row">
                <button
                  type="button"
                  className="book-link rel-seg-pick"
                  onClick={() => setPicked({ kind: "system", key: s.key, label: s.label })}
                  data-testid={`rel-seg-sys-${s.key}`}
                >
                  {s.label}
                </button>
                <span className="k-chip">
                  {s.count} {t("rel.seg.count")}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="gunum-widget__head" style={{ marginTop: "1rem" }}>
          <span className="k-stat__icon k-stat__icon--blue">
            <Icon name="tasks" size={18} />
          </span>
          <span className="gunum-widget__title">{t("rel.seg.saved.title")}</span>
        </div>
        {saved.isLoading && <Loading label={t("rel.loading")} testId="rel-seg-saved-loading" />}
        {saved.error != null && (
          <ErrorNote
            detail={t("crm.loadFailed")}
            onRetry={() => void saved.refetch()}
            busy={saved.isFetching}
            testId="rel-seg-saved-error"
          />
        )}
        {saved.data && saved.data.length === 0 && (
          <p className="k-empty__desc" data-testid="rel-seg-saved-empty">
            {t("rel.seg.empty")}
          </p>
        )}
        {saved.data && saved.data.length > 0 && (
          <ul className="book-list">
            {saved.data
              .filter((s) => s.status === "ACTIVE")
              .map((s) => (
                <li key={s.id} className="k-card k-card--pad-sm book-row">
                  <button
                    type="button"
                    className="book-link rel-seg-pick"
                    onClick={() => setPicked({ kind: "saved", key: s.id, label: s.name })}
                    data-testid={`rel-seg-saved-${s.id}`}
                  >
                    {s.name}
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>

      <SegmentHouseholds picked={picked} />
    </div>
  );
}

function SegmentHouseholds({ picked }: { picked: Picked }) {
  const { t } = useT();
  const system = useSystemSegmentHouseholds(picked?.kind === "system" ? picked.key : null);
  const saved = useSavedSegmentHouseholds(picked?.kind === "saved" ? picked.key : null);
  const active = picked?.kind === "saved" ? saved : system;
  const rows = active.data?.data ?? [];
  // GAP-AUDIT-3 D14 — the read is a deliberate "count + preview" contract (the
  // endpoint takes `limit` only, no cursor), so this list is capped by design.
  // What was WRONG is that the cap was invisible: `hasMore` came back on the wire
  // and was dropped, so an advisor looking at 25 rows of a 148-household segment
  // had no way to know the other 123 existed. The cap is now stated. A real
  // paginated segment browse needs a cursor on the endpoint — backlog, not a
  // number invented here.
  const hasMore = active.data?.hasMore ?? false;
  const total = picked?.kind === "saved" ? (saved.data?.count ?? null) : null;

  return (
    <div className="k-card gunum-widget" data-testid="rel-seg-households">
      <div className="gunum-widget__head">
        <span className="k-stat__icon k-stat__icon--blue">
          <Icon name="household" size={18} />
        </span>
        <span className="gunum-widget__title">
          {picked ? picked.label : t("rel.seg.households.title")}
        </span>
      </div>
      {!picked && <p className="k-empty__desc">{t("rel.seg.pick")}</p>}
      {picked && active.isLoading && (
        <Loading label={t("rel.loading")} testId="rel-seg-hh-loading" />
      )}
      {picked && active.data && rows.length === 0 && (
        <p className="k-empty__desc" data-testid="rel-seg-hh-empty">
          {t("rel.seg.households.empty")}
        </p>
      )}
      {picked && rows.length > 0 && (
        <>
          <ul className="book-list">
            {rows.map((h) => (
              <li key={h.id} className="k-card k-card--pad-sm book-row">
                <NavLink to={`/households/${h.id}`} className="book-link">
                  {h.name}
                </NavLink>
              </li>
            ))}
          </ul>
          {hasMore && (
            <p className="k-empty__desc" data-testid="rel-seg-hh-capped">
              {total === null
                ? `${rows.length} ${t("rel.seg.households.capped")}`
                : `${rows.length} / ${total} ${t("rel.seg.households.capped")}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Görevlerim ───────────────────────────────────────────────────────────────
const TASK_STATUS_LABEL: Record<"OPEN" | "DONE" | "DISMISSED", MessageKey> = {
  OPEN: "rel.task.status.open",
  DONE: "rel.task.status.done",
  DISMISSED: "rel.task.status.dismissed",
};

function TasksTab() {
  const { t } = useT();
  const tasks = useTasks();
  const create = useCreateTask();
  const update = useUpdateTask();
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");

  const submit = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    create.mutate(
      { title: trimmed, note: note.trim() || null },
      {
        onSuccess: () => {
          setTitle("");
          setNote("");
        },
      },
    );
  };

  return (
    <div className="k-card gunum-widget" data-testid="rel-tasks">
      <div className="rel-task-new">
        <input
          className="k-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("rel.task.new.placeholder")}
          maxLength={200}
          data-testid="rel-task-title"
        />
        <input
          className="k-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("rel.task.new.note")}
          maxLength={2000}
          data-testid="rel-task-note"
        />
        <button
          type="button"
          className="k-btn k-btn--primary k-btn--sm"
          disabled={!title.trim() || create.isPending}
          onClick={submit}
          data-testid="rel-task-add"
        >
          {create.isPending ? t("rel.task.adding") : t("rel.task.add")}
        </button>
      </div>

      {tasks.isLoading && <Loading label={t("rel.loading")} testId="rel-task-loading" />}
      {/* THE TAB USED TO RENDER ONLY THE INPUT BOX ON A FAILED READ. `tasks.data`
          is undefined when the query errors, so neither the list nor the empty
          state rendered and nothing said why — an advisor reasonably concludes
          they have no tasks. The neighbouring tabs already used ErrorNote. */}
      {tasks.error != null && (
        <ErrorNote
          detail={t("crm.loadFailed")}
          onRetry={() => void tasks.refetch()}
          busy={tasks.isFetching}
          testId="rel-task-error"
        />
      )}
      {/* create/update fired and reported nothing. */}
      <MutationError
        error={latestError(create, update)}
        fallback={t("crm.actionFailed")}
        offline={t("common.offline")}
      />
      {tasks.data && tasks.data.length === 0 && (
        <Empty
          icon="tasks"
          title={t("rel.task.empty.title")}
          desc={t("rel.task.empty.desc")}
          testId="rel-task-empty"
        />
      )}
      {tasks.data && tasks.data.length > 0 && (
        <ul className="book-list">
          {tasks.data.map((task) => (
            <li
              key={task.id}
              className="k-card k-card--pad-sm book-row"
              data-testid={`rel-task-${task.id}`}
            >
              <div className="book-row__main">
                <span className={task.status === "OPEN" ? "" : "rel-task--settled"}>
                  {task.title}
                </span>
                {task.note && <span className="k-empty__desc">{task.note}</span>}
              </div>
              <span
                className={`k-badge ${task.status === "OPEN" ? "k-badge--standard" : "k-badge--muted"}`}
              >
                {t(TASK_STATUS_LABEL[task.status])}
              </span>
              {task.status === "OPEN" && (
                <div className="k-note__actions">
                  <button
                    type="button"
                    className="k-btn k-btn--soft k-btn--sm"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: task.id, status: "DONE" })}
                    data-testid={`rel-task-done-${task.id}`}
                  >
                    {t("rel.task.done")}
                  </button>
                  <button
                    type="button"
                    className="k-btn k-btn--soft k-btn--sm"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: task.id, status: "DISMISSED" })}
                    data-testid={`rel-task-dismiss-${task.id}`}
                  >
                    {t("rel.task.dismiss")}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────
function Loading({ label, testId }: { label: string; testId: string }) {
  return (
    <div className="k-loading" data-testid={testId}>
      <div className="k-loading__head">
        <span className="k-loading__spinner" />
        <span className="k-loading__label">{label}</span>
      </div>
    </div>
  );
}

function Empty({
  icon,
  title,
  desc,
  testId,
}: {
  icon: "suggestion" | "tasks" | "household";
  title: string;
  desc: string;
  testId: string;
}) {
  return (
    <div className="k-empty" data-testid={testId}>
      <div className="k-empty__icon">
        <Icon name={icon} size={26} />
      </div>
      <p className="k-empty__title">{title}</p>
      <p className="k-empty__desc">{desc}</p>
    </div>
  );
}

function ErrorNote({
  detail,
  onRetry,
  busy,
  testId,
}: {
  detail: string;
  onRetry: () => void;
  busy: boolean;
  testId: string;
}) {
  const { t } = useT();
  return (
    <div className="k-note k-note--error" data-testid={testId}>
      <div className="k-note__row">
        <span className="k-note__icon k-note__icon--error">
          <Icon name="alert-triangle" size={20} />
        </span>
        <div>
          <p className="k-note__title k-note__title--sm">{t("rel.error.title")}</p>
          <p role="alert" className="k-note__desc">
            {detail}
          </p>
          <p className="k-note__desc gunum-reassure">{t("rel.reassure")}</p>
          <div className="k-note__actions">
            <button
              type="button"
              className="k-btn k-btn--soft k-btn--sm"
              disabled={busy}
              onClick={onRetry}
              data-testid={`${testId}-retry`}
            >
              {t("rel.retry")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
